import { useEffect, useState, useRef } from "react";
import { Search, Plus, Truck, FileText, Edit2, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import Swal from "sweetalert2";
import api from "../services/api";
import SupplierForm from "../components/SupplierForm";

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);

  const fetchSuppliers = async (page = 1, searchTerm = search) => {
    try {
      setLoading(true);
      const res = await api.get(`/suppliers/paginated?page=${page}&search=${searchTerm}`);

      if (res.data.data) {
        setSuppliers(res.data.data);
        setCurrentPage(res.data.current_page);
        setTotalPages(res.data.last_page);
        setTotal(res.data.total);
        setFrom(res.data.from);
        setTo(res.data.to);
      } else {
        setSuppliers(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error("Error loading suppliers:", error);
      Swal.fire("Error", "Could not load suppliers", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    setTimeout(() => {
      searchRef.current?.focus();
    }, 100);
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchSuppliers(newPage, search);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      setCurrentPage(1);
      fetchSuppliers(1, value);
    }, 500);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete supplier?",
      text: "The supplier will be deleted (soft delete)",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
      confirmButtonText: "Yes, delete",
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/suppliers/${id}`);
      Swal.fire("Deleted", "Supplier deleted successfully", "success");
      fetchSuppliers(currentPage, search);
    } catch (error) {
      Swal.fire("Error", "Could not delete the supplier", "error");
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <Truck size={28} color="#667eea" />
            <h1 className="card-title">Supplier Management</h1>
          </div>

          {!showForm && (
            <button
              className="btn btn-success"
              onClick={() => {
                setEditingSupplier(null);
                setShowForm(true);
              }}
            >
              <Plus size={18} />
              New Supplier
            </button>
          )}
        </div>

        <div className="card-body">
          {showForm && (
            <SupplierForm
              editingSupplier={editingSupplier}
              onSaved={() => {
                fetchSuppliers(currentPage, search);
                setEditingSupplier(null);
                setShowForm(false);
                setTimeout(() => {
                  searchRef.current?.focus();
                }, 100);
              }}
              onCancel={() => {
                setEditingSupplier(null);
                setShowForm(false);
                setTimeout(() => {
                  searchRef.current?.focus();
                }, 100);
              }}
            />
          )}

          {!showForm && (
            <div className="form-group" style={{ position: "relative" }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#a0aec0",
                  }}
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, phone or email..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  ref={searchRef}
                  style={{ paddingLeft: "44px" }}
                />
              </div>
              {search && (
                <small style={{ color: "#718096", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  🔍 Searching all suppliers...
                </small>
              )}
            </div>
          )}

          {!showForm && (
            <>
              {loading ? (
                <div className="text-center" style={{ padding: "40px" }}>
                  <div className="spinner" style={{ margin: "0 auto" }}></div>
                  <p style={{ marginTop: "16px", color: "#718096" }}>Loading suppliers...</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Contact</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th style={{ textAlign: "center", width: "180px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suppliers.length === 0 ? (
                        <tr>
                          <td colSpan="5">
                            <div className="text-center" style={{ padding: "40px 20px" }}>
                              <FileText size={48} color="#cbd5e0" style={{ marginBottom: "16px" }} />
                              <p style={{ color: "#a0aec0", fontSize: "16px", margin: 0 }}>
                                {search ? "No suppliers found matching your search" : "No suppliers registered yet"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        suppliers.map((supplier) => (
                          <tr key={supplier.id}>
                            <td>
                              <span style={{ fontWeight: 600 }}>{supplier.nombre}</span>
                            </td>
                            <td>{supplier.contacto || "-"}</td>
                            <td>{supplier.telefono || "-"}</td>
                            <td>{supplier.email || "-"}</td>
                            <td>
                              <div className="d-flex gap-2" style={{ justifyContent: "center" }}>
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => {
                                    setEditingSupplier(supplier);
                                    setShowForm(true);
                                  }}
                                  title="Edit supplier"
                                >
                                  <Edit2 size={16} />
                                </button>

                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleDelete(supplier.id)}
                                  title="Delete supplier"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {totalPages > 1 && (
                <div style={{ marginTop: "24px", display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                    <ChevronLeft size={16} />
                  </button>
                  <span style={{ padding: "6px 12px", color: "#718096", fontSize: "14px" }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button className="btn btn-secondary btn-sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}

              {suppliers.length > 0 && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    background: "#f7fafc",
                    borderRadius: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#718096", fontSize: "14px" }}>
                    Showing {from} - {to} of {total} suppliers
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}